import type { TokenOptimizationSummary, TokenOptimizationWindowPoint } from '@ace-tracker/shared';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { chartAxisTickProps, chartLegendProps, chartTooltipProps } from '../chartTheme';
import { DeveloperName, useDeveloperProfiles } from '../settings';
import { ChartCard } from './ChartCard';
import { ExportMenu } from './ExportMenu';
import { EfficiencyGauge, PERCENT_BANDS } from './EfficiencyGauge';
import { KpiCard } from './KpiCard';
import {
  ChartEmptyState,
  compactNumber,
  ReadingGuide,
  wholeNumber,
  type SectionHeadingProps,
} from './OptimizationShared';
import type { SourceHealthInput } from '../health';
import { SourceEmptyState } from './SourceEmptyState';

type HeadroomWindow = TokenOptimizationWindowPoint['window'];

const ALL_WINDOWS: readonly HeadroomWindow[] = ['today', 'last_7_days', 'last_30_days'];
const WINDOW_LABELS: Record<HeadroomWindow, string> = { today: 'Today', last_7_days: 'Last 7 days', last_30_days: 'Last 30 days' };

/**
 * The dedicated Headroom view. Headroom reports rolling windows (today,
 * last 7 / 30 days) from its durable ledger, not a daily history — so this
 * screen compares windows and developers, never a day-by-day trend. All
 * totals come from buildTokenOptimizationSummary; this only lays them out.
 */
export function HeadroomDashboardBody({
  summary,
  SectionHeading,
  health,
}: {
  summary: TokenOptimizationSummary;
  SectionHeading: (props: SectionHeadingProps) => JSX.Element;
  /** Headroom's data health, to explain an empty view. */
  health?: SourceHealthInput;
}) {
  const { label: developerLabel } = useDeveloperProfiles();
  // "Today" only makes sense when the selected range reaches today — otherwise
  // it would label a stale number as current.
  const todayIso = new Date().toISOString().slice(0, 10);
  const rangeIncludesToday = !summary.range.to || summary.range.to.slice(0, 10) >= todayIso;
  const windows = rangeIncludesToday ? ALL_WINDOWS : ALL_WINDOWS.filter((window) => window !== 'today');
  const totals = summary.headroomTotals.filter((total) => windows.includes(total.window));
  const asOf = summary.headroomAsOfDate;
  const coverage = summary.coverage;
  const coveragePercent = coverage.activeAiDevelopers > 0 ? (coverage.headroom / coverage.activeAiDevelopers) * 100 : null;

  if (totals.length === 0) {
    return <SourceEmptyState source="headroom" health={health} />;
  }

  const widest = totals[totals.length - 1];
  const byDeveloper = new Map<string, Map<HeadroomWindow, TokenOptimizationWindowPoint>>();
  for (const row of summary.headroom) {
    const developerWindows = byDeveloper.get(row.developerId) ?? new Map();
    developerWindows.set(row.window, row);
    byDeveloper.set(row.developerId, developerWindows);
  }
  const windowChart = totals.map((total) => ({
    label: WINDOW_LABELS[total.window],
    tokensBefore: total.tokensBefore,
    tokensAfter: Math.max(0, total.tokensBefore - total.tokensSaved),
    tokensSaved: total.tokensSaved,
  }));
  const developerChart = summary.headroom
    .filter((row) => row.window === widest.window)
    .sort((a, b) => b.tokensSaved - a.tokensSaved)
    .map((row) => ({ label: developerLabel(row.developerId), tokensSaved: row.tokensSaved }));

  return (
    <>
      <ReadingGuide>
        Headroom compresses context sent to the model through its proxy and MCP paths and records what it saved in a durable ledger.
        Figures are Headroom's combined proxy and MCP totals for rolling windows — not a daily history — taken from each device's most
        recent report in the selected range; only devices within a day of the latest report are summed.
      </ReadingGuide>

      <section aria-labelledby="headroom-efficiency-heading">
        <div id="headroom-efficiency-heading">
          <SectionHeading title="Efficiency" hint={asOf ? `Tokens saved ÷ tokens before compression · as of ${asOf}` : undefined} />
        </div>
        <div className={`grid grid-cols-1 gap-4 sm:grid-cols-2 ${totals.length === 3 ? 'xl:grid-cols-4' : 'xl:grid-cols-3'}`}>
          {totals.map((total) => (
            <EfficiencyGauge
              key={total.window}
              label={`Savings · ${WINDOW_LABELS[total.window]}`}
              value={total.tokensBefore > 0 ? total.savingsPercent : null}
              bands={PERCENT_BANDS}
              meta={`${compactNumber(total.tokensSaved)} of ${compactNumber(total.tokensBefore)} tokens saved`}
              info="Headroom's tokens saved divided by tokens before compression for this window, proxy and MCP combined."
            />
          ))}
          <EfficiencyGauge
            label="Headroom coverage"
            value={coveragePercent}
            valueLabel={coveragePercent === null ? undefined : `${Math.round(coveragePercent)}%`}
            meta={coverage.activeAiDevelopers > 0 ? `${coverage.headroom} of ${coverage.activeAiDevelopers} active AI developers` : 'No active AI developers in this range'}
            info="Active AI developers (at least one Copilot, Claude Code, or Codex request in the range) who also reported Headroom."
          />
        </div>
      </section>

      <section aria-labelledby="headroom-windows-heading">
        <div id="headroom-windows-heading">
          <SectionHeading title="By window" hint="Summed across reporting developers" />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <KpiCard label={`Tokens saved · ${WINDOW_LABELS[widest.window]}`} value={compactNumber(widest.tokensSaved)} />
          <KpiCard label={`Tokens before · ${WINDOW_LABELS[widest.window]}`} value={compactNumber(widest.tokensBefore)} info="Context tokens Headroom received before compression." />
          <KpiCard label={`Compressions · ${WINDOW_LABELS[widest.window]}`} value={compactNumber(widest.calls)} meta={widest.calls > 0 ? `${compactNumber(widest.tokensSaved / widest.calls)} tokens saved per compression` : undefined} />
          <KpiCard label="Reporting developers" value={wholeNumber(widest.developers)} />
        </div>
        <div className="mt-5 grid grid-cols-1 gap-5 xl:grid-cols-2">
          <ChartCard title="Before vs after compression" caption="Tokens Headroom received, and what remained after compression, per window." height="h-[320px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={windowChart} margin={{ top: 8, right: 12, bottom: 8, left: 4 }}>
                <CartesianGrid stroke="var(--line-soft)" vertical={false} />
                <XAxis dataKey="label" {...chartAxisTickProps} />
                <YAxis {...chartAxisTickProps} width={56} tickFormatter={(v: number) => compactNumber(Number(v))} />
                <Tooltip {...chartTooltipProps} formatter={(value, name) => [wholeNumber(Number(value)), name]} />
                <Legend {...chartLegendProps} />
                <Bar dataKey="tokensBefore" name="Before compression" fill="#99BCDD" radius={[3, 3, 0, 0]} />
                <Bar dataKey="tokensAfter" name="After compression" fill="#0058AB" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
          <ChartCard title={`Tokens saved by developer · ${WINDOW_LABELS[widest.window]}`} caption="Most recent report per device, summed per developer." height="h-[320px]">
            {developerChart.length === 0 ? (
              <ChartEmptyState label="No developer reported this window." />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={developerChart} layout="vertical" margin={{ top: 4, right: 16, bottom: 4, left: 8 }}>
                  <CartesianGrid stroke="var(--line-soft)" horizontal={false} />
                  <XAxis type="number" {...chartAxisTickProps} tickFormatter={(v: number) => compactNumber(Number(v))} />
                  <YAxis type="category" dataKey="label" {...chartAxisTickProps} width={120} />
                  <Tooltip {...chartTooltipProps} formatter={(value) => [wholeNumber(Number(value)), 'Tokens saved']} />
                  <Bar dataKey="tokensSaved" name="Tokens saved" fill="#06D6A0" radius={[0, 3, 3, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </div>
      </section>

      <section aria-labelledby="headroom-developers-heading">
        <div id="headroom-developers-heading">
          <SectionHeading title="By developer" hint="Tokens saved and savings % for each window" />
        </div>
        <div data-export-root className="rounded-card border border-border bg-surface p-5 shadow-sm">
          <ExportMenu title="Headroom by developer" className="-mr-2 -mt-2 mb-1 justify-end" />
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-border text-[11px] uppercase tracking-wide text-text-faint">
                <tr>
                  <th className="pb-2.5 pr-3">Developer</th>
                  {windows.map((window) => (
                    <th key={window} className="pb-2.5 pr-3 text-right">{WINDOW_LABELS[window]}</th>
                  ))}
                  <th className="pb-2.5 pr-3 text-right">Compressions ({WINDOW_LABELS[widest.window]})</th>
                  <th className="pb-2.5 text-right">Devices</th>
                </tr>
              </thead>
              <tbody>
                {[...byDeveloper.entries()].map(([developerId, developerWindows]) => {
                  const devices = Math.max(...[...developerWindows.values()].map((row) => row.devices), 0);
                  return (
                    <tr key={developerId} className="border-b border-border last:border-0">
                      <td className="py-2.5 pr-3"><DeveloperName developerId={developerId} /></td>
                      {windows.map((window) => {
                        const row = developerWindows.get(window);
                        return (
                          <td key={window} className="py-2.5 pr-3 text-right tabular-nums">
                            {row ? (
                              <>
                                {wholeNumber(row.tokensSaved)}
                                <span className="ml-1.5 text-xs text-text-muted">{row.savingsPercent.toFixed(1)}%</span>
                              </>
                            ) : (
                              '—'
                            )}
                          </td>
                        );
                      })}
                      <td className="py-2.5 pr-3 text-right tabular-nums">{wholeNumber(developerWindows.get(widest.window)?.calls ?? 0)}</td>
                      <td className="py-2.5 text-right tabular-nums">{devices}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </>
  );
}
